import { useIsMobile } from "@/hooks/useIsMobile";
import { CalendarCheck, Rocket } from "@phosphor-icons/react/dist/ssr";
import Link from "next/link";
import Image from "next/image";
import googlePreferredSourceBadge from "@/assets/google_preferred_source_badge_dark_en@2x.png";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { useState, useCallback } from "react";
import toast from "react-hot-toast";
import { useReCaptcha } from "next-recaptcha-v3";
import { verifyCaptcha } from "@/services/api/Master";
import { subscribe } from "@/services/api/Newsletter";

export default function Navbar() {
  const isMobile = useIsMobile();

  // -----------------------
  // SUBSCRIBE FORM LOGIC
  // -----------------------
  const [loadingSubscribe, setLoadingSubscribe] = useState(false);
  const { executeRecaptcha } = useReCaptcha();

  const handleFooterSubscribe = useCallback(
    async (event) => {
      event.preventDefault();

      try {
        setLoadingSubscribe(true);

        const email = event.target.email.value;
        const first_name = event.target.first_name.value;
        const last_name = event.target.last_name.value;
        const thisYear = new Date().getFullYear();

        // Recaptcha
        const token = await executeRecaptcha("footer_subscribe");
        const response = await verifyCaptcha(token);

        if (response.data.score > 0.5) {
          const payload = {
            email,
            tags: ["Customer", String(thisYear), "Web"],
          };

          if (first_name) payload.first_name = first_name;
          if (last_name) payload.last_name = last_name;

          try {
            await subscribe(payload);
            toast.success("You are successfully subscribed to our newsletter!");
            event.target.reset();
          } catch (error) {
            if (
              error.response?.data?.message === "User is already subscribed."
            ) {
              toast(error.response.data.message);
            } else {
              toast.error("Subscription failed. Please try again.");
            }
          }
        } else {
          toast.error("Captcha verification failed. Please try again.");
        }

        setLoadingSubscribe(false);
      } catch (err) {
        console.log(err);
        toast.error("Something went wrong.");
        setLoadingSubscribe(false);
      }
    },
    [executeRecaptcha]
  );

  // -----------------------
  // RENDER
  // -----------------------
  return (
    <div className='pt-8 mt-8 border-t border-dashed lg:pt-0 pb-28 lg:pb-0'>
      <div className='container relative flex flex-wrap gap-8 lg:gap-0 lg:divide-x lg:flex-nowrap divide-dashed divide-white/10'>
        <span className='absolute top-0 bottom-0 hidden w-1/2 lg:block bg-gradient-to-br from-white/5 to-transparent left-1/2'></span>

        <div className='relative flex w-full lg:w-1/2 gap-8 lg:p-8 px-0 lg:pl-0 !border-l-0 order-last lg:order-first'>
          <div className='flex flex-wrap flex-1 text-center lg:text-left lg:gap-8 lg:flex-nowrap'>
            <div className='order-1 w-1/2 lg:w-auto'>
              <p className='text-xs opacity-40'>About Lucidity</p>
              <div className='flex flex-col gap-1.5 mt-3 lg:text-lg font-display'>
                <Link
                  href='/about'
                  className='transition-colors hover:text-orange-500'
                >
                  Company
                </Link>
                <Link
                  href='/careers'
                  className='transition-colors hover:text-orange-500'
                >
                  Careers
                </Link>
                <Link
                  href='/contact-us'
                  className='transition-colors hover:text-orange-500'
                >
                  Contact & Support
                </Link>
                <Link
                  href='/email-archive'
                  className='transition-colors hover:text-orange-500'
                >
                  Newsletter Archive
                </Link>
              </div>
            </div>

            <div className='order-last w-full mt-4 lg:flex-1 lg:order-2 lg:mt-0'>
              <div className='flex flex-col gap-2 text-lg font-display'>
                <Link
                  href='https://calendly.com/lucy-viqk'
                  target='_blank'
                  className='inline-flex items-center justify-center px-4 py-2 text-sm font-normal text-white transition-colors rounded-md shrink-0 bg-gradient-to-l from-red-500 to-purple-600'
                >
                  <CalendarCheck weight='bold' className='mr-2 text-base' />
                  Book a Free Demo
                </Link>
                <Link
                  href='https://forms.gle/djJFoYZ7pUtoewNy6'
                  target='_blank'
                  className='inline-flex items-center justify-center px-4 py-2 text-sm font-normal text-white transition-colors rounded-md shrink-0 bg-white/10 hover:bg-white/20'
                >
                  <Rocket weight='bold' className='mr-2 text-base' />
                  Feature my Startup Story
                </Link>
              </div>
            </div>

            <div className='order-3 w-1/2 lg:w-1/5'>
              <p className='text-xs opacity-40'>Socials</p>
              <div className='flex flex-col gap-1 mt-3 font-display'>
                <Link
                  href='https://www.linkedin.com/company/lucidity-insights/'
                  target='_blank'
                  className='transition-colors hover:text-orange-500'
                >
                  LinkedIn
                </Link>
                <Link
                  href='https://twitter.com/lucidityinsight'
                  target='_blank'
                  className='transition-colors hover:text-orange-500'
                >
                  X
                </Link>
                <Link
                  href='https://www.instagram.com/lucidityinsights/'
                  target='_blank'
                  className='transition-colors hover:text-orange-500'
                >
                  Instagram
                </Link>
                <Link
                  href='https://www.youtube.com/@lucidityinsights'
                  target='_blank'
                  className='transition-colors hover:text-orange-500'
                >
                  YouTube
                </Link>
              </div>

              <Link
                href='https://google.com/preferences/source?q=lucidityinsights.com'
                target='_blank'
                rel='noopener noreferrer'
                aria-label='Add Lucidity Insights as a preferred source on Google'
                className='inline-block mt-4 transition-opacity hover:opacity-80'
              >
                <Image
                  src={googlePreferredSourceBadge}
                  alt='Add Lucidity Insights as a preferred source on Google'
                  className='w-auto h-9'
                />
              </Link>
            </div>
          </div>
        </div>

        <div className='relative flex flex-wrap items-center w-full gap-4 px-0 lg:gap-8 lg:p-8 lg:flex-nowrap lg:pr-0 lg:w-1/2'>
          <div>
            <p className='max-w-md text-3xl font-semibold leading-[1.1] tracking-tight font-display'>
              Register for our free weekly newsletter
            </p>
            <p className='max-w-md mt-2 text-xs leading-relaxed lg:mt-4 opacity-30'>
              {`Stay up to date with the latest news, special reports, videos,
                  infobytes, and features on the region's most notable
                  entrepreneurial ecosystems`}
            </p>
          </div>

          <form
            onSubmit={handleFooterSubscribe}
            disabled={loadingSubscribe}
            className='grid grid-cols-12 gap-3'
          >
            <Input
              id='email'
              name='email'
              type='email'
              required
              placeholder='Enter your email'
              disabled={loadingSubscribe}
              className='col-span-12 rounded-lg bg-white/10 border-white/10 placeholder:text-white/40'
            />

            <Input
              id='first_name'
              name='first_name'
              type='text'
              placeholder='First Name'
              disabled={loadingSubscribe}
              className='col-span-6 rounded-lg bg-white/10 border-white/10 placeholder:text-white/40'
            />

            <Input
              id='last_name'
              name='last_name'
              type='text'
              placeholder='Last Name'
              disabled={loadingSubscribe}
              className='col-span-6 rounded-lg bg-white/10 border-white/10 placeholder:text-white/40'
            />

            <Button
              type='submit'
              size='sm'
              disabled={loadingSubscribe}
              className='col-span-12 text-sm'
            >
              {loadingSubscribe ? "Loading..." : "Subscribe Now"}
            </Button>
          </form>
        </div>
      </div>

      {/* BOTTOM BAR */}
      <div className='container flex-wrap items-center justify-between hidden py-4 border-t border-dashed lg:flex lg:flex-nowrap border-white/10'>
        <div className='flex gap-5 text-xs'>
          <Link
            href='/privacy-policy'
            className='transition-colors text-white/30 hover:text-white'
          >
            Privacy Policy
          </Link>
          <Link
            href='/terms-of-service'
            className='transition-colors text-white/30 hover:text-white'
          >
            Terms of Services
          </Link>
          <Link
            href='/dont-sell'
            className='transition-colors text-white/30 hover:text-white'
          >
            Do Not Sell My Info
          </Link>
          <Link
            href='/attribution-instructions'
            className='transition-colors text-white/30 hover:text-white'
          >
            Attribution Instructions
          </Link>
        </div>
        <p className='text-xs opacity-30'>
          © {new Date().getFullYear()} Lucidity Insights. All rights reserved.
        </p>
      </div>
    </div>
  );
}
